import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { wealthRebuildData } from '../data/mockData';

const ScanGreeting = ({ scan }) => {
  const fmtMoney = (n) => `$${Math.round(n / 1000)}K`;
  return (
    <div
      data-testid="wr-scan-greeting"
      style={{
        maxWidth: '860px',
        margin: '0 auto 36px',
        padding: '22px 28px',
        borderRadius: '14px',
        background: 'rgba(212, 175, 55, 0.1)',
        border: '1px solid rgba(212, 175, 55, 0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        textAlign: 'left',
      }}
    >
      <AlertCircle size={22} style={{ color: '#ffd76a', flexShrink: 0, marginTop: '2px' }} />
      <div style={{ color: 'rgba(255,255,255,0.92)', fontSize: '15.5px', lineHeight: '1.7' }}>
        <strong style={{ color: '#ffd76a' }}>
          {scan.firstName ? `${scan.firstName}, your` : 'Your'} scan found {scan.derogatoryMarks} negative item{scan.derogatoryMarks !== 1 ? 's' : ''}
        </strong>
        {scan.avgScore ? <> dragging your {scan.avgScore} average score</> : null}
        {scan.fundingHigh ? <> — and holding back up to <strong style={{ color: '#ffd76a' }}>{fmtMoney(scan.fundingHigh)}</strong> in potential funding</> : null}.
        {' '}The Wealth Rebuild Bundle™ below is the exact system that removes those items and rebuilds your profile.
      </div>
    </div>
  );
};

const WealthRebuildPage = () => {
  const location = useLocation();
  const scan = location.state?.scan;
  const showGreeting = scan && (scan.derogatoryMarks ?? 0) > 0;

  return (
    <div className="dark-page">
      {/* Hero Section */}
      <section className="page-hero wealth-hero">
        <div className="page-hero-content">
          <div className="wealth-badge">PREMIUM PROGRAM</div>
          <h1 className="display-huge page-title">{wealthRebuildData.hero.title}</h1>
          <p className="body-large page-subtitle">
            {wealthRebuildData.hero.subtitle}
          </p>
          <p className="body-medium page-description">
            {wealthRebuildData.hero.description}
          </p>
          {showGreeting && <ScanGreeting scan={scan} />}
        </div>
      </section>

      {/* What's Included */}
      <section className="wealth-included-section">
        <div className="section-header">
          <h2 className="display-large section-title">What's Included</h2>
          <p className="body-medium section-description">
            A complete credit repair engagement — every negative item removed, 75 days flat.
          </p>
        </div>

        <div className="wealth-included-grid">
          {wealthRebuildData.included.map((category, index) => (
            <div key={index} className="wealth-category-card">
              <h3 className="heading-2 category-title">{category.category}</h3>
              <ul className="category-items">
                {category.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="category-item">
                    <CheckCircle2 size={18} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Buy Now CTA */}
      <section style={{ padding: '40px 7.6923%', textAlign: 'center', background: 'var(--bg-primary)' }}>
        <a 
          href={wealthRebuildData.paymentLink}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary"
          style={{ fontSize: '20px', padding: '20px 50px' }}
        >
          Buy Now <ArrowRight size={24} />
        </a>
      </section>

      {/* Who This Is For */}
      <section className="wealth-for-section">
        <div className="section-header">
          <h2 className="display-large section-title">Who This Program Is For</h2>
        </div>

        <div className="wealth-for-grid">
          <div className="wealth-for-card">
            <h3 className="heading-2 for-title">Entrepreneurs</h3>
            <p className="body-medium for-description">
              Building or scaling a business and need both personal credit optimization and business credit infrastructure to access growth capital.
            </p>
          </div>

          <div className="wealth-for-card">
            <h3 className="heading-2 for-title">Real Estate Investors</h3>
            <p className="body-medium for-description">
              Positioning for investment property financing, fix-and-flip funding, or portfolio expansion through improved creditworthiness.
            </p>
          </div>

          <div className="wealth-for-card">
            <h3 className="heading-2 for-title">High-Income Professionals</h3>
            <p className="body-medium for-description">
              Strong income but credit challenges holding you back from premium lending terms, business ventures, or investment opportunities.
            </p>
          </div>

          <div className="wealth-for-card">
            <h3 className="heading-2 for-title">Strategic Rebuilders</h3>
            <p className="body-medium for-description">
              Recovering from financial setbacks and want to rebuild the right way with proper structure, not quick fixes.
            </p>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="wealth-guarantee-section">
        <div className="guarantee-content">
          <h2 className="display-medium guarantee-title">{wealthRebuildData.guarantee.title}</h2>
          <p className="body-large guarantee-text">{wealthRebuildData.guarantee.description}</p>
        </div>
      </section>

      {/* Why Choose This Program */}
      <section className="wealth-why-section">
        <div className="section-header">
          <h2 className="display-large section-title">Why Choose the Wealth Rebuild Program</h2>
        </div>

        <div className="wealth-why-grid">
          <div className="wealth-why-card">
            <h3 className="heading-2 why-card-title">Hard 75-Day Deadline</h3>
            <p className="body-medium why-card-description">
              Every dispute cycle is completed inside 75 days. No open-ended “we&apos;ll keep trying” — a clear window, then a clear result.
            </p>
          </div>

          <div className="wealth-why-card">
            <h3 className="heading-2 why-card-title">Money-Back Guarantee</h3>
            <p className="body-medium why-card-description">
              If we do not remove a single negative item inside 75 days, we refund every dollar. No arbitration, no fine print, no held-back fees.
            </p>
          </div>

          <div className="wealth-why-card">
            <h3 className="heading-2 why-card-title">Multi-Layer Disputes</h3>
            <p className="body-medium why-card-description">
              We hit the bureaus AND the original data furnishers using CROA/FCRA method-of-verification and debt-validation escalation — the tactics that actually force deletions.
            </p>
          </div>

          <div className="wealth-why-card">
            <h3 className="heading-2 why-card-title">Fully Compliant</h3>
            <p className="body-medium why-card-description">
              No CPNs, no rented lines, no ID-swap schemes. Legal, defensible removals that hold up when a lender or underwriter looks twice.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section wealth-cta">
        <div className="cta-content">
          <h2 className="display-large cta-title">Ready to Rebuild Your Credit?</h2>
          <p className="body-large cta-description">
            Every negative item removed in 75 days — or your money back. Lock in your seat and let&apos;s get to work.
          </p>
          <a 
            href={wealthRebuildData.paymentLink}
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn-primary cta-button"
            style={{ fontSize: '18px', padding: '18px 40px', marginBottom: '15px' }}
          >
            Buy Now
            <ArrowRight size={20} />
          </a>
          <p className="cta-note body-small">
            Or <a href="https://calendly.com/thecollinsacademynew/30min" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-gold)' }}>schedule a call</a> to learn more
          </p>
        </div>
      </section>
    </div>
  );
};

export default WealthRebuildPage;